/**
 * BoundedImageryProvider — wraps UrlTemplateImageryProvider to return blank
 * transparent tiles for zoom levels below the actual data range.
 *
 * This prevents Cesium from issuing 404 requests for tiles at z=0..minZoom-1
 * when using WebMercatorTilingScheme with global-scheme ortho tiles that only
 * exist at a specific zoom range (e.g. z=12-23).
 *
 * The `rectangle` is set on the PROVIDER ONLY (to limit tile enumeration).
 * Do NOT also set it on the ImageryLayer — that triggers a Cesium 1.137 bug
 * in _createTileImagerySkeletons where clippedImageryRectangle is undefined.
 */

import * as Cesium from 'cesium';

// Cached 1x1 transparent canvas — avoids tainted-canvas issues that
// can occur with data-URI HTMLImageElement in some browsers.
let blankCanvasPromise: Promise<HTMLCanvasElement> | null = null;

function getBlankCanvas(): Promise<HTMLCanvasElement> {
  if (!blankCanvasPromise) {
    blankCanvasPromise = Promise.resolve().then(() => {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      return canvas;
    });
  }
  return blankCanvasPromise;
}

/**
 * Create a UrlTemplateImageryProvider for global Web Mercator tiles that only
 * exist within a specific zoom range. Returns a transparent canvas for any
 * tile request below `minZoom`, avoiding 404s entirely.
 *
 * @param url        Tile URL template with {z}/{x}/{y}
 * @param minZoom    Lowest zoom level where real tiles exist
 * @param maxZoom    Highest zoom level where real tiles exist
 * @param rectangle  Geographic bounds — constrains tile enumeration
 * @param tileSize   Tile pixel dimensions (default 256)
 */
export function createBoundedImageryProvider(
  url: string,
  minZoom: number,
  maxZoom: number,
  rectangle: Cesium.Rectangle,
  tileSize: number = 256,
): Cesium.UrlTemplateImageryProvider {
  const provider = new Cesium.UrlTemplateImageryProvider({
    url,
    tilingScheme: new Cesium.WebMercatorTilingScheme(),
    minimumLevel: 0,
    maximumLevel: maxZoom,
    rectangle, // Constrains tile enumeration to data bounds
    tileWidth: tileSize,
    tileHeight: tileSize,
    credit: 'CoreSkan',
  });

  // Override requestImage to intercept below-minZoom requests
  const originalRequestImage = provider.requestImage.bind(provider);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (provider as any).requestImage = function (
    x: number,
    y: number,
    level: number,
    request?: Cesium.Request,
  ) {
    if (level < minZoom) {
      return getBlankCanvas();
    }
    return originalRequestImage(x, y, level, request);
  };

  // Suppress errors for edge tiles that don't exist
  provider.errorEvent.addEventListener((err: Cesium.TileProviderError) => {
    err.retry = false;
  });

  return provider;
}
